import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { MODAL_ID } from 'constants/settings';
import { RolesDropdownService } from 'service/dropdownServices/RolesDropdown';
import { CompanyTypeService } from 'service/CompanyTypeService';
import { showErrorMessage, showSuccessMessage } from 'helpers/notifications';

import '../DeleteDialog/deleteDialog.scss';
import styles from './styles.module.scss';

interface CompanyRole {
  id: string;
  value: string;
}

interface CompanyTypeRequestProps {
  companyId: string;
  /** Текущий тип компании — строка `company_roles` */
  currentType?: string;
  onClose: () => void;
}

const COMMENT_MAX_LENGTH = 1024;

/** Заявка администратору на смену типа компании: сам тип попап не меняет */
const CompanyTypeRequest = ({ companyId, currentType, onClose }: CompanyTypeRequestProps) => {
  const { t } = useTranslation();
  const lang = useSelector((state: any) => state.settings.lang);

  const [roles, setRoles] = useState<CompanyRole[]>([]);
  const [roleId, setRoleId] = useState('');
  const [comment, setComment] = useState('');
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    RolesDropdownService.getRoles(lang).then((res: any) => {
      // Неудачный запрос резолвится Error (см. RolesDropdownService), поэтому проверяем данные
      const items: CompanyRole[] = Array.isArray(res?.data) ? res.data : [];

      // Текущий тип из списка убираем: заявка на него ничего не меняет
      setRoles(items.filter((role) => role.value !== currentType));
    });
  }, [lang, currentType]);

  const handleSubmit = () => {
    if (!roleId) {
      showErrorMessage(t('profile.company_type.request.required'));
      return;
    }

    setIsSending(true);
    CompanyTypeService.requestTypeChange(companyId, { role_id: roleId, comment: comment.trim() || undefined })
      .then(() => {
        showSuccessMessage(t('profile.company_type.request.success'));
        onClose();
      })
      .catch((error) => {
        setIsSending(false);
        // 409 — предыдущая заявка ещё не рассмотрена, второй раз отправлять нечего
        showErrorMessage(
          error?.response?.status === 409
            ? t('profile.company_type.request.pending')
            : t('profile.company_type.request.error'),
        );
      });
  };

  return ReactDOM.createPortal(
    <div className="overlay">
      <div className="overlay__wrapper">
        <div className="popup">
          <div className="confirm__wrapper">
            <ul className={`confirm__list ${styles.list}`}>
              <li className="confirm__item">
                <h2 className="confirm__title title--yellow">{t('profile.company_type.request.title')}</h2>
              </li>
              <li className="confirm__item">
                <p className={styles.message}>{t('profile.company_type.request.message')}</p>
              </li>
              <li className="confirm__item">
                {/* Документы у типов разные, поэтому вместе с типом обнуляются файлы и верификация */}
                <p className={styles.warning}>{t('profile.company_type.request.warning')}</p>
              </li>
              <li className="confirm__item">
                <div className={styles.field}>
                  <span className={styles.label}>{t('profile.company_type.request.current')}</span>
                  <span className={styles.currentType}>{currentType || '-'}</span>
                </div>
              </li>
              <li className="confirm__item">
                <label className={styles.field}>
                  <span className={styles.label}>{t('profile.company_type.request.desired')}</span>
                  <select
                    className={styles.select}
                    value={roleId}
                    onChange={(event) => setRoleId(event.target.value)}
                  >
                    <option value="">{t('profile.company_type.request.desired_placeholder')}</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.value}
                      </option>
                    ))}
                  </select>
                </label>
              </li>
              <li className="confirm__item">
                <label className={styles.field}>
                  <span className={styles.label}>{t('profile.company_type.request.comment')}</span>
                  <textarea
                    className={styles.comment}
                    value={comment}
                    maxLength={COMMENT_MAX_LENGTH}
                    rows={3}
                    placeholder={t('profile.company_type.request.comment_placeholder')}
                    onChange={(event) => setComment(event.target.value)}
                  />
                </label>
              </li>
              <li className={`confirm__item ${styles.actions}`}>
                <button className="popup__button" type="button" disabled={isSending} onClick={handleSubmit}>
                  {t('profile.company_type.request.submit')}
                </button>
                <button className="popup__button popup__button--cancel" type="button" onClick={onClose}>
                  {t('profile.company_type.request.cancel')}
                </button>
              </li>
            </ul>
            <button
              className="popup__close-button payment-confirm__close-button"
              type="button"
              onClick={onClose}
            />
          </div>
        </div>
      </div>
    </div>,
    document.getElementById(MODAL_ID) as HTMLElement,
  );
};

export default CompanyTypeRequest;
